import { CircularProgress } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";

import { useSaveSeed, useSeed } from "@/api/creativesphere";
import { castingCodec } from "@/api/creativesphere/codecs/casting";
import { CastingAssetBuilder } from "@/features/GenLab/components/CastingAssetBuilder";

export const Route = createFileRoute(
  "/_auth/genlab/$siteId/presets/casting/edit/$presetId",
)({
  component: EditCastingAssetRoute,
});

function EditCastingAssetRoute() {
  const { siteId, presetId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useSeed(siteId, presetId);
  const saveSeed = useSaveSeed(siteId);

  const goBackToStudio = () => {
    void navigate({ to: "/genlab/$siteId", params: { siteId } });
  };

  useEffect(() => {
    if (isError) goBackToStudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  if (isLoading) return <CircularProgress />;
  if (!data) return null;

  const asset = castingCodec.fromSeed(data);

  const handleSave = (changes: {
    name: string;
    notes?: string;
    thumbnailUrl?: string;
  }) => {
    saveSeed.mutate(
      {
        slug: asset.id,
        body: {
          seed_type: castingCodec.seedType,
          name: changes.name,
          thumbnail_url: changes.thumbnailUrl,
          tags: asset.tags,
          params: castingCodec.toParams({
            instructions: asset.instructions,
            notes: changes.notes,
          }),
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar("Information saved", { variant: "success" });
          goBackToStudio();
        },
        onError: () => {
          enqueueSnackbar("Couldn't save changes. Please try again.", {
            variant: "error",
          });
        },
      },
    );
  };

  return (
    <CastingAssetBuilder
      asset={asset}
      onSave={handleSave}
      onBack={goBackToStudio}
    />
  );
}
